import { z } from "zod";
import { stripe } from "#/lib/stripe";
import { uniq } from "lodash";
import { Logger } from "#/lib/logging";
import Stripe from "stripe";

const PriceSchema = z.discriminatedUnion("isMetered", [
  z.object({
    isMetered: z.literal(true),
    id: z.string(),
    currency: z.string(),
    unitAmount: z.number(),
    recurringInterval: z.string(),
    meterId: z.string()
  }),
  z.object({
    isMetered: z.literal(false),
    id: z.string(),
    currency: z.string(),
    unitAmount: z.number(),
    recurringInterval: z.never().optional(),
    meterId: z.never().optional()
  })
]);

export type PriceType = z.infer<typeof PriceSchema>;

export const ProductSchema = z.object({
  id: z.string(),
  tier: z.enum([ "free", "pro", "enterprise" ]),
  name: z.string(),
  description: z.string(),
  numCommands: z.number(),
  pricePerCommand: z.number(),
  hasMeteredPrice: z.boolean(),
  prices: PriceSchema.array()
});

export type ProductType = z.infer<typeof ProductSchema>;

type GetProduct =
  | {
    priceIds: string[],
    productId?: never
  }
  | {
    productId: string,
    priceIds?: never
  };

export const getProduct = async ({
  productId, priceIds
}: GetProduct): Promise<ProductType> => {
  let product: Stripe.Product;
  let prices: Stripe.Price[];
  if (priceIds) {
    prices = await Promise.all(priceIds.map(priceId => stripe.prices.retrieve(priceId)));
    if (prices.some(price => !price?.product)) {
      throw Logger.error({ error: new Error("Price does not have a product"), metadata: { priceIds } });
    }
    const productIds = prices.map(price => {
      if (typeof price.product === "string") {
        return price.product;
      }
      return price.product.id;
    });
    if (uniq(productIds).length !== 1) {
      throw Logger.error({ error: new Error("Prices do not belong to the same product"), metadata: { priceIds, productIds } });
    }
    product = await stripe.products.retrieve(productIds[0]);
  } else if (productId) {
    product = await stripe.products.retrieve(productId);
    const { data } = await stripe.prices.list({
      product: product.id
    });
    if (!data.length) {
      throw Logger.error({ error: new Error("Product does not have any prices"), metadata: { productId: product.id } });
    }
    prices = data;
  } else {
    throw Logger.error({ error: new Error("Either productId or priceId must be provided"), metadata: { productId, priceIds } });
  }

  if (!("num_commands" in product.metadata)) {
    throw Logger.error({ error: new Error("Product does not have num_commands metadata"), metadata: { productId: product.id, priceIds: prices.map(price => price.id) } });
  }
  const numCommands = Number.parseInt(product.metadata.num_commands, 10);
  if (!Number.isInteger(numCommands)) {
    throw Logger.error({ error: new Error("Product num_commands metadata is not an integer"), metadata: { productId: product.id, priceIds: prices.map(price => price.id) } });
  }
  const {
    recurringPrice, unitPrice
  } = prices.reduce((acc, price) => {
    if (price.lookup_key?.endsWith("recurring")) {
      acc.recurringPrice = price;
    } else {
      acc.unitPrice = price;
    }
    return acc;
  }, { recurringPrice: null as Stripe.Price | null, unitPrice: null as Stripe.Price | null });

  if (!recurringPrice) {
    throw Logger.error({ error: new Error("Product does not have a recurring price"), metadata: { productId: product.id, priceIds: prices.map(price => price.id) } });
  }

  const unitAmountParsed = Number.parseFloat(recurringPrice.unit_amount_decimal ?? "0") / 100;
  const pricePerCommand = unitAmountParsed / numCommands;

  const mappedPrices = prices
    .filter(price => (
      !!price?.unit_amount_decimal
      && !!price.recurring?.interval
      && !!price.currency
    ))
    .map((price): PriceType => {
      if (price.recurring?.meter) {
        return {
          isMetered: true,
          id: price.id,
          currency: price.currency,
          unitAmount: Number.parseFloat(price.unit_amount_decimal!) / 100,
          recurringInterval: price.recurring?.interval!,
          meterId: price.recurring?.meter
        };
      }
      return {
        isMetered: false,
        id: price.id,
        currency: price.currency,
        unitAmount: Number.parseFloat(price.unit_amount_decimal!) / 100
      };
    });

  if (mappedPrices.length !== prices.length) {
    throw Logger.error({ error: new Error("Some prices are missing required fields"), metadata: { prices, mappedPrices } });
  }

  return {
    id: product.id,
    name: product.name,
    tier: product.name === "Pro" ? "pro" : product.name === "Enterprise" ? "enterprise" : "free",
    numCommands: numCommands,
    pricePerCommand: pricePerCommand,
    description: product.description ?? "",
    hasMeteredPrice: !!unitPrice?.recurring?.meter,
    prices: mappedPrices
  };
};